/**
 * SupabasePresetStore — PresetStore backed by Supabase Postgres + RLS.
 *
 * Each row in the `presets` table belongs to one user (via `user_id`).
 * Row-level security on the server ensures clients can only read/write
 * their own rows; the RLS policies are defined in
 * supabase/migrations/0001_init.sql.
 *
 * The canonical Preset JSON is stored in the `data` JSONB column. A handful
 * of columns (id, user_id, name, updated_at) are duplicated for indexing
 * and sorting — they're re-derived from the Preset on every write.
 */

import type { SupabaseClient } from '@supabase/supabase-js';
import type { AuthManager } from '@/auth/AuthManager';
import { migratePreset } from '@ac-slayer/synth-presets';
import type { Preset } from '@ac-slayer/synth-presets';
import type { PresetStore } from '@ac-slayer/synth-presets';

interface PresetRow {
  id: string;
  user_id: string;
  name: string;
  data: Preset;
  updated_at: string;
}

export class SupabasePresetStore implements PresetStore {
  constructor(
    private readonly client: SupabaseClient,
    private readonly auth: AuthManager,
  ) {}

  async put(preset: Preset): Promise<void> {
    const userId = this.requireUserId();
    const { error } = await this.client.from('presets').upsert({
      id: preset.id,
      user_id: userId,
      name: preset.name,
      data: preset,
      updated_at: new Date(preset.updatedAt).toISOString(),
    });
    if (error) {
      throw new Error(`Could not save preset: ${error.message}`);
    }
  }

  async get(id: string): Promise<Preset | undefined> {
    const userId = this.auth.currentUser?.id;
    if (!userId) return undefined;
    const { data, error } = await this.client
      .from('presets')
      .select('data')
      .eq('id', id)
      .eq('user_id', userId)
      .maybeSingle();
    if (error) {
      throw new Error(`Could not load preset: ${error.message}`);
    }
    if (!data) return undefined;
    const row = data as Pick<PresetRow, 'data'>;
    return migratePreset(row.data) ?? undefined;
  }

  async delete(id: string): Promise<void> {
    const userId = this.requireUserId();
    const { error } = await this.client
      .from('presets')
      .delete()
      .eq('id', id)
      .eq('user_id', userId);
    if (error) {
      throw new Error(`Could not delete preset: ${error.message}`);
    }
  }

  async list(): Promise<Preset[]> {
    const userId = this.auth.currentUser?.id;
    if (!userId) return [];
    const { data, error } = await this.client
      .from('presets')
      .select('data')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false });
    if (error) {
      throw new Error(`Could not list presets: ${error.message}`);
    }
    const rows = (data ?? []) as Pick<PresetRow, 'data'>[];
    const out: Preset[] = [];
    for (const row of rows) {
      const p = migratePreset(row.data);
      if (p) out.push(p);
    }
    return out;
  }

  private requireUserId(): string {
    const id = this.auth.currentUser?.id;
    if (!id) throw new Error('Must be signed in to modify presets.');
    return id;
  }
}
